import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft, CircleAlert, RefreshCw } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import SupportTicketDetails from '../support/tickets/SupportTicketDetails';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Page = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 1.25rem 2rem 2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  align-self: flex-start;
  height: 30px;
  padding: 0 0.625rem 0 0.5rem;
  margin-left: -0.5rem;
  border-radius: 8px;
  color: var(--color-text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--color-hover-overlay);
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const Bone = styled.div`
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '14px'};
  border-radius: ${({ $r }) => $r || '6px'};
  background: linear-gradient(
    90deg,
    var(--color-bg-tertiary) 0%,
    var(--color-hover-overlay) 50%,
    var(--color-bg-tertiary) 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap || '0.625rem'};
`;

const SkeletonLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 1.25rem;
  margin-top: 0.75rem;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SkeletonCard = styled.div`
  padding: 1.125rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  background: var(--color-bg-primary);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  max-width: 420px;
  margin: 3rem auto;
  padding: 2rem 1.5rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  background: var(--color-bg-primary);
  text-align: center;

  > svg {
    color: #dc3545;
  }

  h1 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  p {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--color-text-secondary);
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  margin-top: 0.25rem;
  padding: 0 0.875rem;
  border: 1px solid var(--color-btn-primary-border);
  border-radius: 8px;
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.88;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const DetailsSkeleton = () => (
  <div aria-busy="true" aria-label="Carregando ticket">
    <SkeletonStack>
      <SkeletonStack $gap="0.375rem" style={{ flexDirection: 'row' }}>
        <Bone $w="72px" $h="24px" />
        <Bone $w="88px" $h="24px" $r="999px" />
        <Bone $w="64px" $h="24px" $r="999px" />
      </SkeletonStack>
      <Bone $w="min(560px, 80%)" $h="28px" />
      <Bone $w="min(360px, 60%)" $h="14px" />
    </SkeletonStack>
    <SkeletonLayout>
      <SkeletonStack $gap="1.25rem">
        <SkeletonCard>
          <Bone $w="96px" />
          <Bone />
          <Bone $w="92%" />
          <Bone $w="70%" />
        </SkeletonCard>
        <SkeletonCard>
          <Bone $w="120px" />
          <Bone $h="96px" $r="10px" />
        </SkeletonCard>
      </SkeletonStack>
      <SkeletonCard>
        <Bone $w="110px" />
        {[0, 1, 2, 3, 4].map((row) => (
          <SkeletonStack key={row} style={{ flexDirection: 'row' }}>
            <Bone $w="88px" $h="34px" />
            <Bone $h="34px" $r="8px" />
          </SkeletonStack>
        ))}
      </SkeletonCard>
    </SkeletonLayout>
  </div>
);

const TicketDetailsScreen = ({ backTo, backLabel, backState, onListUpdate, onResetSelection }) => {
  const { id } = useParams();
  const { ticketDetails, detailsLoading, detailsError, fetchTicketDetails, users } = useApp();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (id) fetchTicketDetails(id);
  }, [id, fetchTicketDetails]);

  const handleTicketUpdate = async () => {
    if (id) await fetchTicketDetails(id);
  };

  const isCurrentTicket = Boolean(ticketDetails) && String(ticketDetails.id) === String(id);
  const showError = !isCurrentTicket && Boolean(detailsError) && !detailsLoading;

  return (
    <Page>
      <BackLink to={backTo} state={backState}>
        <ArrowLeft size={15} strokeWidth={2} aria-hidden />
        {backLabel}
      </BackLink>

      {isCurrentTicket ? (
        <SupportTicketDetails
          ticket={ticketDetails}
          users={users || []}
          onTicketUpdate={handleTicketUpdate}
          onListUpdate={onListUpdate}
          onResetSelection={onResetSelection}
        />
      ) : showError ? (
        <ErrorCard role="alert">
          <CircleAlert size={22} strokeWidth={2} aria-hidden />
          <h1>Não foi possível carregar este ticket</h1>
          <p>Verifique sua conexão e tente novamente. Se o problema continuar, o ticket pode ter sido removido.</p>
          <RetryButton type="button" onClick={() => fetchTicketDetails(id)}>
            <RefreshCw size={14} strokeWidth={2} aria-hidden />
            Tentar novamente
          </RetryButton>
        </ErrorCard>
      ) : (
        <DetailsSkeleton />
      )}
    </Page>
  );
};

TicketDetailsScreen.propTypes = {
  backTo: PropTypes.string.isRequired,
  backLabel: PropTypes.string.isRequired,
  backState: PropTypes.object,
  onListUpdate: PropTypes.func,
  onResetSelection: PropTypes.func,
};

export default TicketDetailsScreen;
